/**
 * Browser detection utilities for Safari-specific fixes
 */

/**
 * Detect if the current browser is Safari
 */
export function isSafari(): boolean {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isSafariUA =
    /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent);
  const isWebKit = "WebKitAppearance" in document.documentElement.style;

  // Check for Safari on iOS
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  return isSafariUA || (isWebKit && isIOS) || /^((?!chrome|android).)*safari/i.test(userAgent);
}

/**
 * Detect if Safari is in private browsing mode
 * Note: This is not 100% reliable but can help with some issues
 */
export function isSafariPrivateMode(): boolean {
  if (!isSafari()) return false;

  try {
    // Try to use localStorage
    localStorage.setItem("__safari_private_test__", "1");
    localStorage.removeItem("__safari_private_test__");
    return false;
  } catch (e) {
    // If we can't write to localStorage, we're likely in private mode
    return true;
  }
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = "__localStorage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Check if sessionStorage is available
 */
export function isSessionStorageAvailable(): boolean {
  try {
    const test = "__sessionStorage_test__";
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Detect if a field was filled by autofill
 * Safari autofill doesn't always trigger onChange events immediately
 */
export function detectAutofill(input: HTMLInputElement): boolean {
  // Check if the input has a value but no recent user interaction
  if (input.value && !input.matches(":focus")) {
    // Check if the value was set programmatically (autofill indicator)
    const style = window.getComputedStyle(input);
    // Safari autofill often changes the background color
    const backgroundColor = style.backgroundColor;
    const isAutofillBackground =
      backgroundColor !== "rgba(0, 0, 0, 0)" &&
      backgroundColor !== "transparent" &&
      backgroundColor !== "rgb(255, 255, 255)";

    return isAutofillBackground;
  }
  return false;
}

/**
 * Wait for autofill to complete
 * Safari autofill can be delayed, so we wait a bit
 */
export async function waitForAutofill(input: HTMLInputElement, timeout = 1000): Promise<boolean> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = 50;

    const check = () => {
      if (input.value && detectAutofill(input)) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime > timeout) {
        resolve(false);
        return;
      }

      setTimeout(check, checkInterval);
    };

    check();
  });
}
